import { FxKind, HeroId, Slot } from "@rivalwatch/shared";
import type { Cue, Out } from "../audio/engine.ts";
import { baked } from "../audio/bake.ts";
import { TRIM } from "../audio/levels.ts";
import { bell, brass, click, clinks, cymbal, drive, filterOut, fm, later, mallet, noise, semis, thump, tone, trim, vary, whoosh } from "../audio/synth.ts";

const D4 = 293.66;
const hz = (semi: number, base = D4) => base * 2 ** (semi / 12);
const SCALE = { heal: [0, 2, 4, 7, 9] } as const;

/**
 * The kit palette. Role sets the material — tanks are brass and scrap, damage is steel,
 * supports are glass — and each weapon is built ink → color → halftone.
 */

export interface FireCtx {
  /** Secondary fire mode (Mender's Bombard). */
  alt?: boolean;
  /** Ultimate running (Visor lock-on, Whole Hog). */
  ult?: boolean;
  scoped?: boolean;
}

// ------------------------------------------------------------------ weapons

/** Volt — Pulse Rifle. Ten rounds a second, so it has to be tight and never fatiguing. */
export const pulseRifle =
  (visor: boolean): Cue =>
  (raw) => {
    // Ten a second: keep the sub free for explosions and ultimates.
    const o = filterOut(raw, "highpass", 95, 0.6);
    const p = vary(1, 0.035);
    click(o, 0.32, 4200, 0.003);
    tone(o, { type: "square", f: 330 * p, f1: 150 * p, glide: 0.04, gain: 0.06, d: 0.05, lp: 2400 });
    noise(o, { color: "white", type: "bandpass", f: 1900 * p, f1: 1100, q: 1.1, gain: 0.28, d: 0.05 });
    thump(o, 0.24, 125 * p, 60, 0.07);
    noise(o, { color: "pink", type: "lowpass", f: 900, gain: 0.06, a: 0.004, d: 0.15 });
    if (visor) tone(o, { f: 2640, f1: 3300, glide: 0.03, gain: 0.045, d: 0.05, delay: 0.012 });
    return 0.25;
  };

function rocketLaunch(o: Out, size: number, pitch: number) {
  thump(o, 0.4 * size, 140 * pitch, 60 * pitch, 0.09);
  whoosh(o, 0.5 * size, 480 * pitch, 2400 * pitch, 0.24);
  noise(o, { color: "white", type: "highpass", f: 3500, gain: 0.1 * size, d: 0.12 });
  noise(o, { color: "crackle", type: "bandpass", f: 2600, q: 0.7, gain: 0.35 * size, d: 0.14 });
  return 0.4;
}

/** Ember — Rocket Launcher: a heavy tube thunk and a whoosh that leaves with the rocket. */
const rocketLauncher: Cue = (o) => {
  thump(o, 0.6, 95, 46, 0.13);
  click(o, 0.3, 2400, 0.006);
  rocketLaunch(o, 1, 0.8);
  noise(o, { color: "brown", type: "lowpass", f: 500, gain: 0.28, d: 0.3 });
  return 0.5;
};

/** Anvil — Hexblades: three blades of light — a bright FM "shing" fanned across a whole tone. */
export const hexblades: Cue = (o) => {
  const p = vary(1, 0.025);
  for (let i = 0; i < 3; i++) fm(later(o, i * 0.017), { f: 1350 * p * semis(i * 2), ratio: 2.76, index: 3, index1: 0.35, gain: 0.065, d: 0.26 });
  whoosh(o, 0.26, 1800, 4400, 0.13, 0, 1.4);
  tone(o, { f: 2800 * p, gain: 0.03, d: 0.12, delay: 0.01 });
  click(o, 0.12, 5000, 0.003);
  return 0.4;
};

/** Tether — Scattergun: a driven blast, junk rattling in the barrel, then the pump. */
const scattergun =
  (hog: boolean): Cue =>
  (o) => {
    const p = vary(1, 0.03);
    click(o, 0.6, 2000, 0.008);
    noise(drive(o, 2.5), { color: "pink", type: "lowpass", f: 3200 * p, f1: 450, gain: 0.5, d: hog ? 0.12 : 0.2 });
    thump(o, 0.75, 88 * p, 42, hog ? 0.12 : 0.2);
    clinks(o, hog ? 2 : 5, 0.05, 0.12, 1500, 3800);
    if (!hog) {
      click(o, 0.1, 1100, 0.012, 0.34);
      click(o, 0.12, 850, 0.014, 0.43);
      fm(later(o, 0.43), { f: 420, ratio: 1.9, index: 2, index1: 0, gain: 0.04, d: 0.06 });
    }
    return hog ? 0.3 : 0.6;
  };

/** Mender — Repair Orb: a bubbly "bloop" that rises (it's good news for someone). */
export const repairOrb: Cue = (o) => {
  const f = vary(380, 0.04);
  tone(o, { f, f1: f * 2, glide: 0.07, gain: 0.2, d: 0.1 });
  tone(o, { type: "triangle", f: f * 4, gain: 0.045, d: 0.06, delay: 0.05 });
  click(o, 0.1, 3000, 0.003);
  noise(o, { color: "pink", type: "bandpass", f: 1400, q: 2, gain: 0.08, d: 0.08 });
  return 0.2;
};

/** Mender — Bombard: small, bright and fast; a gadget, not a rifle. */
export const bombard: Cue = (raw) => {
  const o = filterOut(raw, "highpass", 120, 0.6);
  const p = vary(1, 0.05);
  click(o, 0.2, 4500, 0.003);
  tone(o, { type: "triangle", f: 1500 * p, f1: 620 * p, glide: 0.04, gain: 0.11, d: 0.05 });
  noise(o, { color: "white", type: "bandpass", f: 3000 * p, q: 1.2, gain: 0.11, d: 0.035 });
  thump(o, 0.1, 180, 90, 0.05);
  return 0.15;
};

/** Sprout — Bio-Rifle, hip fire: a pneumatic "thup" with a biotic glint on F#. */
const bioRifle: Cue = (o) => {
  click(o, 0.3, 3000, 0.004);
  thump(o, 0.35, 230, 110, 0.06);
  noise(o, { color: "pink", type: "bandpass", f: 1900, f1: 900, q: 1.2, gain: 0.3, d: 0.08 });
  fm(o, { f: hz(28), ratio: 2, index: 1.2, index1: 0, gain: 0.05, d: 0.18, delay: 0.01 });
  return 0.25;
};

/** Sprout — Bio-Rifle, scoped: a crack, and a glass ring that hangs in the air. */
const bioRifleScoped: Cue = (o) => {
  click(o, 0.55, 3000, 0.005);
  tone(o, { f: 1800, f1: 900, glide: 0.05, gain: 0.12, d: 0.06 });
  fm(o, { f: 2600, ratio: 1.414, index: 1.6, index1: 0, gain: 0.05, d: 0.45 });
  noise(o, { color: "pink", type: "lowpass", f: 1500, gain: 0.3, d: 0.14 });
  thump(o, 0.3, 110, 55, 0.1);
  return 0.6;
};

const fireKey = (hero: HeroId, v: FireCtx) =>
  hero === HeroId.CaptainAmericano ? (v.ult ? "volt-visor" : "volt") : hero === HeroId.Bulk ? (v.ult ? "hog" : "tether") : hero === HeroId.PocketRaccoon ? (v.alt ? "bombard" : "orb") : hero === HeroId.LunaSnowcone ? (v.scoped ? "scoped" : "dart") : hero === HeroId.IonMan ? "ember" : "anvil";

/** A trigger pull, baked: five renders per weapon mode, a hair of pitch jitter per shot. */
export const fire = (hero: HeroId, v: FireCtx = {}) => baked(`fire:${fireKey(hero, v)}`, () => fireLive(hero, v), { variants: 5, jitter: 0.015 });

export const genericBlaster = (key: string): Cue => baked(`fire:stub:${key}`, () => trim(TRIM.fire.volt, pulseRifle(false)), { variants: 5, jitter: 0.015 });
export const doctorFire = baked("fire:doctor-estranged", () => trim(TRIM.fire.anvil, hexblades), { variants: 5, jitter: 0.015 });
export const pocketRepairFire = baked("fire:pocket-raccoon:orb", () => trim(TRIM.fire.orb, repairOrb), { variants: 5, jitter: 0.015 });
export const pocketBombardFire = baked("fire:pocket-raccoon:bombard", () => trim(TRIM.fire.bombard, bombard), { variants: 5, jitter: 0.015 });

function fireLive(hero: HeroId, v: FireCtx): Cue {
  switch (hero) {
    case HeroId.CaptainAmericano:
      return trim(TRIM.fire.volt, pulseRifle(!!v.ult));
    case HeroId.IonMan:
      return trim(TRIM.fire.ember, rocketLauncher);
    case HeroId.DoctorEstranged:
      return trim(TRIM.fire.anvil, hexblades);
    case HeroId.Bulk:
      return trim(v.ult ? TRIM.fire.hog : TRIM.fire.tether, scattergun(!!v.ult));
    case HeroId.PocketRaccoon:
      return v.alt ? trim(TRIM.fire.bombard, bombard) : trim(TRIM.fire.orb, repairOrb);
    case HeroId.LunaSnowcone:
      return v.scoped ? trim(TRIM.fire.scoped, bioRifleScoped) : trim(TRIM.fire.dart, bioRifle);
    default:
      return trim(TRIM.fire.volt, pulseRifle(false));
  }
}

// ------------------------------------------------------------------ abilities

const helixVolley: Cue = (o) => {
  for (let i = 0; i < 3; i++) rocketLaunch(later(o, i * 0.045), 0.55, 1.25);
  return 0.45;
};

/** A metal canister set down: hollow clank, then the hiss of it opening. */
const canister = (o: Out) => {
  fm(o, { f: 380, ratio: 1.8, index: 3, index1: 0, gain: 0.12, d: 0.18 });
  thump(o, 0.3, 140, 70, 0.08);
  noise(o, { color: "white", type: "highpass", f: 4000, gain: 0.08, a: 0.05, d: 0.45, delay: 0.08 });
  return 0.7;
};

const bioticField: Cue = (o) => {
  canister(o);
  for (const [i, s] of [0, 4, 7].entries()) bell(later(o, 0.12 + i * 0.05), hz(s + 12), 0.2, 0.3);
  return 1.2;
};

const jumpJet: Cue = (o) => {
  thump(o, 0.7, 75, 38, 0.3);
  noise(o, { color: "pink", type: "bandpass", f: 350, f1: 2800, q: 0.7, gain: 0.7, a: 0.05, d: 0.45 });
  noise(o, { color: "crackle", type: "bandpass", f: 2000, q: 0.6, gain: 0.5, d: 0.35 });
  noise(o, { color: "white", type: "highpass", f: 5000, gain: 0.08, a: 0.02, d: 0.3 });
  return 0.8;
};

/** Concussive Blast launch: a pressure "thoomp". The detonation is its own explosion cue. */
const concussionLaunch: Cue = (o) => {
  thump(o, 0.5, 210, 90, 0.12);
  whoosh(o, 0.35, 400, 1300, 0.2);
  return 0.35;
};

/** Gateway: the portal tears open — a low swell, a swirl, the Lydian tritone ringing on top. */
export const gateway: Cue = (o) => {
  tone(o, { type: "sawtooth", f: 90, f1: 300, glide: 0.45, gain: 0.06, a: 0.1, d: 0.5, lp: 900 });
  noise(o, { color: "pink", type: "bandpass", f: 500, f1: 2200, q: 2.5, gain: 0.35, a: 0.15, d: 0.5 });
  bell(later(o, 0.25), hz(18), 0.5, 0.5);
  bell(later(o, 0.3), hz(19), 0.5, 0.45);
  return 1.4;
};

/** Maelstrom wind-up (the burst arrives as a Singularity explosion). */
export const maelstromCast: Cue = (o) => {
  noise(o, { color: "pink", type: "bandpass", f: 300, f1: 1400, q: 1.5, gain: 0.5, a: 0.12, d: 0.12 });
  fm(o, { f: hz(-17), ratio: 1.5, index: 3, index1: 6, gain: 0.12, a: 0.1, d: 0.15 });
  return 0.35;
};

/** Chain Hook: the chain uncoils — a rapid run of links, and air. */
const chainThrow: Cue = (o) => {
  clinks(o, 9, 0.07, 0.25, 1800, 4200);
  whoosh(o, 0.4, 600, 2000, 0.22);
  fm(o, { f: 300, ratio: 2.3, index: 3, index1: 0, gain: 0.08, d: 0.12 });
  return 0.45;
};

const snareThrow: Cue = (o) => {
  fm(o, { f: 520, ratio: 2.4, index: 4, index1: 0, gain: 0.1, d: 0.14 });
  whoosh(o, 0.25, 700, 1500, 0.2, 0.03);
  return 0.35;
};

export const jetDash: Cue = (o) => {
  noise(o, { color: "pink", type: "bandpass", f: 2200, f1: 600, q: 0.8, gain: 0.6, a: 0.01, d: 0.22 });
  thump(o, 0.25, 160, 80, 0.07);
  noise(o, { color: "crackle", type: "highpass", f: 2500, gain: 0.3, d: 0.12 });
  return 0.35;
};

/** Rebirth Beacon: a clunk, then it powers up with a rising heal arpeggio. */
export const beaconDeploy: Cue = (o) => {
  canister(o);
  tone(o, { f: 300, f1: 900, glide: 0.3, gain: 0.07, a: 0.05, d: 0.3, delay: 0.1 });
  SCALE.heal.forEach((s, i) => mallet(later(o, 0.2 + i * 0.06), hz(s + 12), 0.1, 0.6));
  return 1;
};

/** Sleep Dart: a thwip, then a whistle that falls from F# to D — drowsy. */
const dartThrow: Cue = (o) => {
  click(o, 0.3, 4000, 0.003);
  whoosh(o, 0.45, 1500, 3800, 0.1);
  thump(o, 0.2, 260, 130, 0.05);
  tone(o, { f: hz(28), f1: hz(24), glide: 0.25, gain: 0.06, d: 0.3, delay: 0.02 });
  return 0.45;
};

const grenadeThrow: Cue = (o) => {
  fm(o, { f: 2900, ratio: 1.5, index: 1.2, index1: 0, gain: 0.07, d: 0.1 });
  whoosh(o, 0.25, 600, 1400, 0.25);
  return 0.35;
};

// Ultimate activation layers. The hero's theme plays over these (see playTheme).

const visorOn: Cue = (o) => {
  tone(o, { type: "sawtooth", f: 180, f1: 820, glide: 0.25, gain: 0.06, d: 0.3, lp: 2500 });
  for (let i = 0; i < 3; i++) tone(o, { f: 2400 + i * 300, gain: 0.05, d: 0.04, delay: 0.25 + i * 0.07 });
  click(o, 0.2, 3000, 0.004);
  return 0.6;
};

const barrageOn: Cue = (o) => {
  noise(o, { color: "brown", type: "lowpass", f: 300, f1: 1400, gain: 0.5, a: 0.3, d: 0.3 });
  tone(o, { type: "sawtooth", f: 70, f1: 160, glide: 0.5, gain: 0.08, a: 0.1, d: 0.5, lp: 700 });
  return 0.9;
};

const hogOn: Cue = (o) => {
  tone(drive(o, 3), { type: "sawtooth", f: 55, f1: 140, glide: 0.5, gain: 0.1, a: 0.05, d: 0.55, lp: 900 });
  clinks(o, 6, 0.06, 0.4, 900, 2600);
  return 0.8;
};

export const amplifierOn: Cue = (o) => {
  tone(o, { f: 200, f1: 1200, glide: 0.6, gain: 0.08, a: 0.2, d: 0.5 });
  [0, 4, 7, 12, 16].forEach((s, i) => mallet(later(o, 0.1 + i * 0.07), hz(s + 12), 0.1, 0.7));
  thump(later(o, 0.45), 0.4, 90, 45, 0.3);
  return 1.1;
};

const surgeOn: Cue = (o) => {
  whoosh(o, 0.4, 500, 3000, 0.3);
  thump(later(o, 0.22), 0.5, 100, 45, 0.25);
  [0, 6, 7, 11].forEach((s, i) => bell(later(o, 0.22 + i * 0.04), hz(s + 24), 0.3, 0.5));
  return 1.2;
};

/** Cast sound for an ability slot. The Ultimate entry is the activation layer under the theme. */
export function ability(hero: HeroId, slot: Slot): Cue | undefined {
  const k = `${hero}:${slot}`;
  const table: Record<string, Cue> = {
    [`${HeroId.CaptainAmericano}:${Slot.Secondary}`]: trim(TRIM.ability.helix, helixVolley),
    [`${HeroId.CaptainAmericano}:${Slot.Ability2}`]: trim(TRIM.ability.bioticField, bioticField),
    [`${HeroId.CaptainAmericano}:${Slot.Ultimate}`]: trim(TRIM.ability.visor, visorOn),
    [`${HeroId.IonMan}:${Slot.Ability1}`]: trim(TRIM.ability.jumpJet, jumpJet),
    [`${HeroId.IonMan}:${Slot.Ability2}`]: trim(TRIM.ability.concussion, concussionLaunch),
    [`${HeroId.IonMan}:${Slot.Ultimate}`]: trim(TRIM.ability.barrage, barrageOn),
    [`${HeroId.DoctorEstranged}:${Slot.Ability3}`]: trim(TRIM.ability.gateway, gateway),
    [`${HeroId.DoctorEstranged}:${Slot.Ability2}`]: trim(TRIM.ability.maelstrom, maelstromCast),
    [`${HeroId.Bulk}:${Slot.Ability2}`]: trim(TRIM.ability.chain, chainThrow),
    [`${HeroId.Bulk}:${Slot.Ability1}`]: trim(TRIM.ability.snare, snareThrow),
    [`${HeroId.Bulk}:${Slot.Ultimate}`]: trim(TRIM.ability.hog, hogOn),
    [`${HeroId.PocketRaccoon}:${Slot.Ability1}`]: trim(TRIM.ability.jetDash, jetDash),
    [`${HeroId.PocketRaccoon}:${Slot.Ability2}`]: trim(TRIM.ability.beacon, beaconDeploy),
    [`${HeroId.PocketRaccoon}:${Slot.Ultimate}`]: trim(TRIM.ability.amplifier, amplifierOn),
    [`${HeroId.LunaSnowcone}:${Slot.Ability2}`]: trim(TRIM.ability.sleepDart, dartThrow),
    [`${HeroId.LunaSnowcone}:${Slot.Ability1}`]: trim(TRIM.ability.grenade, grenadeThrow),
    [`${HeroId.LunaSnowcone}:${Slot.Ultimate}`]: trim(TRIM.ability.surge, surgeOn),
  };
  return table[k];
}

export const doctorGateway = trim(TRIM.ability.gateway, gateway);
export const doctorMaelstrom = trim(TRIM.ability.maelstrom, maelstromCast);
export const pocketJetDash = trim(TRIM.ability.jetDash, jetDash);
export const pocketBeacon = trim(TRIM.ability.beacon, beaconDeploy);
export const pocketAmplifier = trim(TRIM.ability.amplifier, amplifierOn);

// ------------------------------------------------------------------ handling

const clack = (o: Out, at: number, f: number, g: number) => {
  click(o, g, f, 0.008, at);
  fm(later(o, at), { f: f * 0.28, ratio: 1.7, index: 2, index1: 0, gain: g * 0.25, d: 0.05 });
};

/** Reload, choreographed across the hero's reload time. */
export const reload = (hero: HeroId, seconds: number) => baked(`reload:${hero}`, () => reloadLive(hero, seconds), { variants: 2, jitter: 0.01 });

export const genericReload = (seconds: number): Cue => baked(`reload:stub:${seconds}`, () => trim(10, reloadGenericLive(seconds)), { variants: 2, jitter: 0.01 });
export const pocketReload = (seconds: number): Cue => baked("reload:pocket-raccoon", () => trim(14.5, reloadPocketLive(seconds)), { variants: 2, jitter: 0.01 });

function reloadGenericLive(seconds: number): Cue {
  return (o) => {
    clack(o, 0.2 * seconds, 1700, 0.18);
    clack(o, 0.8 * seconds, 2300, 0.16);
    return seconds + 0.2;
  };
}

function reloadPocketLive(seconds: number): Cue {
  return (o) => {
    tone(o, { type: "sawtooth", f: 200, f1: 900, glide: 0.5 * seconds, gain: 0.03, a: 0.05, d: 0.5 * seconds, lp: 1800, delay: 0.1 * seconds });
    clack(o, 0.8 * seconds, 2600, 0.18);
    return seconds + 0.2;
  };
}

function reloadLive(hero: HeroId, seconds: number): Cue {
  const s = seconds;
  const reloadTrim = hero === HeroId.PocketRaccoon ? 14.5 : hero === HeroId.DoctorEstranged ? 0 : 10;
  return trim(reloadTrim, (o) => {
    switch (hero) {
      case HeroId.CaptainAmericano:
        clack(o, 0.08 * s, 1800, 0.22);
        noise(o, { color: "pink", type: "bandpass", f: 1500, q: 1.5, gain: 0.06, a: 0.03, d: 0.12, delay: 0.12 * s });
        clack(o, 0.55 * s, 1300, 0.3);
        clack(o, 0.82 * s, 2400, 0.22);
        clack(o, 0.87 * s, 2000, 0.2);
        break;
      case HeroId.IonMan:
        whoosh(o, 0.12, 400, 900, 0.25, 0.2 * s);
        thump(o, 0.3, 120, 60, 0.08, 0.62 * s);
        clack(o, 0.62 * s, 900, 0.25);
        clack(o, 0.88 * s, 2200, 0.2);
        break;
      case HeroId.Bulk:
        for (let i = 0; i < 4; i++) clack(o, (0.18 + i * 0.13) * s, 1500 + i * 60, 0.16);
        clack(o, 0.82 * s, 1100, 0.2);
        clack(o, 0.9 * s, 850, 0.24);
        break;
      case HeroId.PocketRaccoon:
        tone(o, { type: "sawtooth", f: 200, f1: 900, glide: 0.5 * s, gain: 0.03, a: 0.05, d: 0.5 * s, lp: 1800, delay: 0.1 * s });
        clack(o, 0.8 * s, 2600, 0.18);
        break;
      case HeroId.LunaSnowcone:
        clack(o, 0.22 * s, 1700, 0.2);
        noise(o, { color: "pink", type: "bandpass", f: 1200, q: 2, gain: 0.05, d: 0.1, delay: 0.3 * s });
        clack(o, 0.7 * s, 1400, 0.24);
        clack(o, 0.86 * s, 2800, 0.16);
        break;
      default:
        clack(o, 0.18 * s, 1500, 0.16);
        clack(o, 0.82 * s, 2200, 0.18);
        break;
    }
    return s + 0.2;
  });
}

const meleeLive: Cue = trim(TRIM.handling.melee, (o) => {
  whoosh(o, 0.45, 700, 2600, 0.14);
  noise(o, { color: "pink", type: "lowpass", f: 600, gain: 0.12, a: 0.03, d: 0.08 });
  return 0.25;
});

const dryFireLive: Cue = trim(TRIM.handling.dryFire, (o) => {
  click(o, 0.14, 1400, 0.01);
  fm(o, { f: 900, ratio: 2.1, index: 1, index1: 0, gain: 0.04, d: 0.04 });
  return 0.1;
});

export const scope =
  (into: boolean): Cue =>
  trim(TRIM.handling.scope, (o) => {
    click(o, 0.08, 3000, 0.004);
    tone(o, { f: into ? 1200 : 1500, f1: into ? 1550 : 1150, glide: 0.05, gain: 0.03, d: 0.06 });
    return 0.1;
  });

// ------------------------------------------------------------------ projectiles & world

/** A round cracking past your head. */
const whizLive: Cue = trim(TRIM.impact.whiz, (o) => {
  click(o, 0.18, 4000, 0.003);
  noise(o, { color: "white", type: "bandpass", f: 5200, f1: 1800, q: 2, gain: 0.3, a: 0.004, d: 0.09 });
  return 0.15;
});

/** Bullet or blade striking the Foundry: a small steel tick. */
const ricochetLive: Cue = trim(TRIM.impact.ricochet, (o) => {
  const p = vary(1, 0.2);
  click(o, 0.16, 3500 * p, 0.004);
  fm(o, { f: 2400 * p, ratio: 1.9, index: 1.4, index1: 0, gain: 0.04, d: 0.08 });
  return 0.15;
});

export const orbBounce = (n: number) => baked("bounce", () => orbBounceLive(0), { variants: 2, jitter: 0.01, rate: semis(n * 3) });

const orbBounceLive =
  (n: number): Cue =>
  trim(TRIM.impact.bounce, (o) => {
    const f = 700 * semis(n * 3);
    tone(o, { f, f1: f * 1.5, glide: 0.06, gain: 0.12, d: 0.08 });
    fm(o, { f: f * 2, ratio: 1.01, index: 0.8, index1: 0, gain: 0.03, d: 0.12 });
    return 0.2;
  });

const hookHitLive: Cue = trim(TRIM.impact.hook, (o) => {
  fm(o, { f: 600, ratio: 2.33, index: 6, index1: 0.2, gain: 0.16, d: 0.3 });
  thump(o, 0.5, 130, 55, 0.12);
  clinks(o, 6, 0.06, 0.2, 1500, 3500);
  noise(o, { color: "pink", type: "bandpass", f: 900, f1: 400, q: 1, gain: 0.2, a: 0.02, d: 0.35, delay: 0.05 });
  return 0.6;
});

const snareArmedLive: Cue = trim(TRIM.impact.snareArmed, (o) => {
  click(o, 0.12, 2000, 0.006);
  click(o, 0.14, 1600, 0.006, 0.09);
  fm(later(o, 0.09), { f: 700, ratio: 2.4, index: 2, index1: 0, gain: 0.04, d: 0.1 });
  return 0.25;
});

/** A trap snapping shut on someone's ankle. */
const snareSnap: Cue = (o) => {
  click(o, 0.6, 1800, 0.01);
  fm(o, { f: 720, ratio: 2.4, index: 5, index1: 0.3, gain: 0.14, d: 0.35 });
  thump(o, 0.45, 150, 60, 0.12);
  return 0.5;
};

const teleport: Cue = (o) => {
  tone(o, { f: 300, f1: 1400, glide: 0.15, gain: 0.1, d: 0.18 });
  whoosh(o, 0.35, 500, 3000, 0.2);
  bell(later(o, 0.12), hz(30), 0.3, 0.45);
  return 0.9;
};

const orbPop: Cue = (o) => {
  tone(o, { f: 900, f1: 1400, glide: 0.05, gain: 0.1, d: 0.07 });
  noise(o, { color: "white", type: "bandpass", f: 2000, q: 1.5, gain: 0.1, d: 0.05 });
  return 0.15;
};

const bladeTink: Cue = (o) => {
  fm(o, { f: vary(3200, 0.08), ratio: 1.9, index: 1.5, index1: 0, gain: 0.07, d: 0.12 });
  click(o, 0.12, 5000, 0.003);
  return 0.2;
};

export const impact = (kind: FxKind) => baked(`impact:${kind}`, () => impactLive(kind), { variants: 4, jitter: 0.05 });

function impactLive(kind: FxKind): Cue {
  switch (kind) {
    case FxKind.Pellet:
      return trim(TRIM.impact.blade, bladeTink);
    case FxKind.Spore:
      return trim(TRIM.impact.orb, orbPop);
    case FxKind.Hook:
      return trim(TRIM.impact.snare, snareSnap);
    case FxKind.Singularity:
      return trim(TRIM.impact.teleport, teleport);
    default:
      return ricochetLive;
  }
}

/** High explosive: ink crack, driven body, sub drop, debris, a long rolling tail. */
const boom =
  (size: number, pitch = 1): Cue =>
  (o) => {
    click(o, 0.5, 1500, 0.01);
    noise(drive(o, 2), { color: "pink", type: "lowpass", f: 2600 * pitch, f1: 260, gain: 0.45 * size + 0.2, d: 0.3 + 0.25 * size });
    thump(o, 0.75 * size + 0.2, 80 * pitch, 30, 0.35 + 0.3 * size);
    noise(o, { color: "crackle", type: "bandpass", f: 2400, q: 0.6, gain: 0.4 * size, a: 0.01, d: 0.5 });
    noise(o, { color: "brown", type: "lowpass", f: 380, gain: 0.35 * size, a: 0.02, d: 0.9 + size * 0.6 });
    return 1.4 + size;
  };

/** Concussive Blast: all pressure, almost no crack. */
const concussion: Cue = (o) => {
  thump(o, 0.95, 140, 38, 0.3);
  noise(o, { color: "pink", type: "lowpass", f: 900, gain: 0.5, d: 0.18 });
  whoosh(o, 0.4, 300, 1400, 0.35, 0.02);
  return 0.8;
};

/** Bio-Grenade: glass shattering into a fizz. */
const bioBurst: Cue = (o) => {
  for (let i = 0; i < 9; i++) fm(o, { f: 2800 + Math.random() * 3600, ratio: 1.3 + Math.random(), index: 1.5, index1: 0, gain: 0.04, d: 0.08 + Math.random() * 0.1, delay: Math.random() * 0.12 });
  noise(o, { color: "white", type: "bandpass", f: 5000, q: 1.2, gain: 0.18, a: 0.01, d: 0.55 });
  thump(o, 0.35, 120, 60, 0.12);
  noise(o, { color: "crackle", type: "highpass", f: 3000, gain: 0.3, a: 0.02, d: 0.6 });
  return 0.9;
};

/** Maelstrom: dark magic released — a growl of FM under a thud. */
const maelstrom: Cue = (o) => {
  thump(o, 0.8, 90, 32, 0.4);
  fm(drive(o, 2), { f: 55, ratio: 1.5, index: 6, index1: 0.5, gain: 0.25, d: 0.8 });
  noise(o, { color: "pink", type: "bandpass", f: 1200, f1: 250, q: 1.2, gain: 0.45, d: 0.5 });
  bell(later(o, 0.05), hz(18), 0.3, 0.4);
  return 1.4;
};

/** Soul Sever: the eye opens — a choir-ish chord on the Lydian tritone, a vast low bloom. */
const soulSever: Cue = (o) => {
  thump(o, 1, 70, 26, 0.9);
  fm(drive(o, 2), { f: 36.7, ratio: 1.5, index: 7, index1: 0.4, gain: 0.3, d: 1.6 });
  for (const s of [-24, -17, -6, 7]) brass(later(o, 0.03), hz(s), 1.2, 0.55);
  for (const [i, s] of [18, 19, 24].entries()) bell(later(o, 0.1 + i * 0.05), hz(s), 0.6, 0.45);
  cymbal(o, 0.6, 2.2);
  return 2.8;
};

export const explosion = (kind: FxKind, radius: number) => baked(`boom:${kind}:${Math.round(radius)}`, () => explosionLive(kind, radius), { variants: 3, jitter: 0.03 });

function explosionLive(kind: FxKind, radius: number): Cue {
  switch (kind) {
    case FxKind.Rocket:
      return radius < 2.4 ? trim(TRIM.boom.helix, boom(Math.min(1, radius / 2.8), 1.25)) : trim(TRIM.boom.rocket, boom(1));
    case FxKind.Missile:
      return trim(TRIM.boom.missile, boom(0.45, 1.35));
    case FxKind.Concussion:
      return trim(TRIM.boom.concussion, concussion);
    case FxKind.Spore:
      return trim(TRIM.boom.bio, bioBurst);
    case FxKind.Singularity:
      return radius >= 10 ? trim(TRIM.boom.soulSever, soulSever) : trim(TRIM.boom.maelstrom, maelstrom);
    default:
      return trim(TRIM.boom.rocket, boom(Math.min(1, radius / 3)));
  }
}

const missileLaunchLive: Cue = trim(TRIM.impact.missileLaunch, (o) => {
  whoosh(o, 0.3, 900, 2600, 0.12);
  click(o, 0.1, 3000, 0.004);
  return 0.2;
});

/** Health pack: a quick bright climb up the heal pentatonic. */
export const pickup =
  (large: boolean): Cue =>
  trim(TRIM.world.pickup, (o) => {
    const steps = large ? [0, 4, 7, 12, 16] : [0, 4, 7, 12];
    steps.forEach((s, i) => bell(later(o, i * 0.045), hz(s + 12), 0.15, 0.55));
    whoosh(o, 0.15, 800, 2400, 0.2);
    return 0.9;
  });

/** Someone brought back by a Rebirth Beacon: a slow rise and a warm chord. */
export const revive: Cue = trim(TRIM.world.revive, (o) => {
  whoosh(o, 0.3, 300, 2400, 0.8);
  [0, 7, 12, 16, 19].forEach((s, i) => bell(later(o, 0.25 + i * 0.09), hz(s), 0.3, 0.55));
  for (const s of [-12, -5, 4]) brass(later(o, 0.55), hz(s), 0.9, 0.45);
  return 2;
});

/** Sleep Dart landed: a three-note lullaby falls. */
export const lullaby: Cue = trim(TRIM.world.lullaby, (o) => {
  [19, 16, 12].forEach((s, i) => bell(later(o, i * 0.16), hz(s), 0.3, 0.55));
  whoosh(o, 0.08, 1400, 500, 0.5, 0.05);
  return 1.2;
});

/** Surge landing on its target. */
export const surged: Cue = trim(TRIM.world.surged, (o) => {
  thump(o, 0.45, 90, 45, 0.25);
  whoosh(o, 0.35, 400, 2800, 0.3);
  [0, 4, 6, 11, 12].forEach((s, i) => bell(later(o, 0.08 + i * 0.035), hz(s + 24), 0.25, 0.5));
  return 1.1;
});

export const wardUp: Cue = trim(TRIM.world.wardUp, (o) => {
  tone(o, { f: 180, f1: 620, glide: 0.18, gain: 0.08, d: 0.25 });
  bell(later(o, 0.05), hz(18), 0.3, 0.4);
  noise(o, { color: "pink", type: "bandpass", f: 1800, f1: 4000, q: 2, gain: 0.12, a: 0.05, d: 0.2 });
  return 0.8;
});

export const wardDown: Cue = trim(TRIM.world.wardDown, (o) => {
  noise(o, { color: "white", type: "bandpass", f: 3000, f1: 700, q: 1.5, gain: 0.12, d: 0.25 });
  tone(o, { f: 500, f1: 200, gain: 0.04, d: 0.2 });
  return 0.4;
});

export const wardBreak: Cue = trim(TRIM.world.wardBreak, (o) => {
  for (let i = 0; i < 14; i++) fm(o, { f: 2200 + Math.random() * 4500, ratio: 1.2 + Math.random() * 1.4, index: 1.4, index1: 0, gain: 0.05, d: 0.1 + Math.random() * 0.2, delay: Math.random() * 0.18 });
  thump(o, 0.4, 160, 60, 0.15);
  noise(o, { color: "white", type: "highpass", f: 4000, gain: 0.2, d: 0.4 });
  return 0.9;
});

export const sprintStart: Cue = trim(TRIM.world.sprint, (o) => {
  whoosh(o, 0.18, 500, 1400, 0.2);
  noise(o, { color: "pink", type: "bandpass", f: 700, q: 1.2, gain: 0.08, a: 0.01, d: 0.1, delay: 0.05 });
  return 0.3;
});

/** Each end of a Gateway opening: a short swirl and the tritone glint. */
export const portalOpen: Cue = trim(TRIM.impact.portal, (o) => {
  noise(o, { color: "pink", type: "bandpass", f: 700, f1: 2400, q: 3, gain: 0.25, a: 0.08, d: 0.35 });
  bell(later(o, 0.1), hz(18), 0.3, 0.35);
  return 0.9;
});

export const melee = baked("melee", () => meleeLive, { variants: 3 });
export const dryFire = baked("dry", () => dryFireLive, { variants: 2 });
export const whiz = baked("whiz", () => whizLive, { variants: 4, jitter: 0.06 });
export const ricochet = baked("ricochet", () => ricochetLive, { variants: 6, jitter: 0.08 });
export const hookHit = baked("hook-hit", () => hookHitLive, { variants: 2 });
export const snareArmed = baked("snare-armed", () => snareArmedLive, { variants: 2 });
export const missileLaunch = baked("missile-launch", () => missileLaunchLive, { variants: 4, jitter: 0.04 });
